import { LayoutGrid, Plus } from "lucide-react"
import StyledSelectDropdown from "../StyledSelectDropdown"
import { FormField } from "./CreateJobFields"

type LanguageRequirement = {
    languageId: number | null
    speakingLevelId: string | null
    readingWritingLevelId: string | null
}

type LanguageRequirementsSectionProps = {
    isEditMode: boolean
    languageRequirements: LanguageRequirement[]
    languages: any[]
    languagesStatus: string
    speakingLevels: Array<{ value: string; label: string }>
    speakingLevelsStatus: string
    readingWritingLevels: Array<{ value: string; label: string }>
    readingWritingLevelsStatus: string
    addLanguageRequirement: () => void
    removeLanguageRequirement: (index: number) => void
    handleLanguageRequirementChange: (index: number, field: "languageId" | "speakingLevelId" | "readingWritingLevelId", value: string) => void
}

export const LanguageRequirementsSection = ({
    isEditMode,
    languageRequirements,
    languages,
    languagesStatus,
    speakingLevels,
    speakingLevelsStatus,
    readingWritingLevels,
    readingWritingLevelsStatus,
    addLanguageRequirement,
    removeLanguageRequirement,
    handleLanguageRequirementChange,
}: LanguageRequirementsSectionProps) => (
    <section className="mb-10">
        <div className="bg-[#E6EDDD] p-4 flex items-center justify-between rounded-md mb-6">
            <div className="flex items-center gap-3">
                <LayoutGrid className="w-5 h-5 text-gray-700" />
                <h2 className="font-bold text-gray-800 tracking-tight">Language Requirements</h2>
            </div>
            {!isEditMode && (
                <Plus className="w-5 h-5 text-gray-700 cursor-pointer" onClick={addLanguageRequirement} />
            )}
        </div>

        {languageRequirements.map((requirement, index) => (
            <div key={index} className="grid grid-cols-3 gap-6 mb-4 items-end">
                <FormField label="Language" required>
                    <StyledSelectDropdown
                        options={languages}
                        value={requirement.languageId || ""}
                        onChange={(value) => handleLanguageRequirementChange(index, "languageId", value)}
                        placeholder="Select language"
                        disabled={languagesStatus === "loading" || isEditMode}
                    />
                </FormField>
                <FormField label="Speaking level" required>
                    <StyledSelectDropdown
                        options={speakingLevels.map((level) => ({ id: level.value, name: level.label }))}
                        value={requirement.speakingLevelId || ""}
                        onChange={(value) => handleLanguageRequirementChange(index, "speakingLevelId", value)}
                        placeholder="Select speaking level"
                        disabled={speakingLevelsStatus === "loading" || isEditMode}
                    />
                </FormField>
                <FormField label="Writing / reading Level" required>
                    <StyledSelectDropdown
                        options={readingWritingLevels.map((level) => ({ id: level.value, name: level.label }))}
                        value={requirement.readingWritingLevelId || ""}
                        onChange={(value) => handleLanguageRequirementChange(index, "readingWritingLevelId", value)}
                        placeholder="Select reading/writing level"
                        disabled={readingWritingLevelsStatus === "loading" || isEditMode}
                    />
                </FormField>
                {languageRequirements.length > 1 && !isEditMode && (
                    <div className="flex justify-end col-span-3">
                        <button
                            type="button"
                            className="text-red-400 text-xs mt-1 font-medium"
                            onClick={() => removeLanguageRequirement(index)}
                        >
                            Remove
                        </button>
                    </div>
                )}
            </div>
        ))}
        {!isEditMode && (
            <p className="mt-2 text-paragraph font-bold text-gray-500">To add more Requirements click the + icon.</p>
        )}
    </section>
)
